import { Box, Dialog, IconButton, Paper, Stack, useTheme } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { RootState } from "@/store/store";
import { closeLocationDialog } from "@/store/uiSlice";
import GoogleMapReact from "google-map-react";
import { useState, useRef, useCallback, useEffect } from "react";
import { setDeliveryLocation } from "@/features/order/slice";
import { Cancel01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import useOrganizations from "@/features/organizations/useOrganizations";
import useGetDistance from "@/hooks/useGetDistance";
import { useUpdateOrderMutation } from "@/features/organizations/api/ordersApi";
import useSnackbar from "@/hooks/useSnackbar";
import LoadingButton from "@mui/lab/LoadingButton";
import { useFetchBranchQuery } from "@/features/organizations/api/branchApi";
import { skipToken } from "@reduxjs/toolkit/dist/query";

const GoogleMapReactAny = GoogleMapReact as any;
const LocationDialog = () => {
  const dispatch = useAppDispatch();
  const { palette } = useTheme();
  const order = useAppSelector((state: RootState) => state.order);
  const isLocationDialogOpen = useAppSelector((state: RootState) => state.ui.isLocationDialogOpen);
  const organizationId = useAppSelector((state: RootState) => state.ui.activeOrganization);
  const activeBranchId = useAppSelector((state: RootState) => state.ui.activeBranch);
  const { currentOrganization } = useOrganizations();
  const { calculate } = useGetDistance();
  const [updateOrder] = useUpdateOrderMutation();
  const [openSnackbar] = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);

  const { data: branches = [] } = useFetchBranchQuery(organizationId ?? skipToken, {
    skip: !organizationId,
  });

  const activeBranch = activeBranchId ? branches.find((b) => b.id === activeBranchId) : null;

  const [location, setLocation] = useState<{ lat: number; lng: number }>({ lat: 12.115, lng: -86.2362 });

  const mapRef = useRef<any>(null);
  const mapsRef = useRef<any>(null);
  const directionsRendererRef = useRef<any>(null);

  useEffect(() => {
    setLocation({
      lat: order.deliveryLocation?.latitude
        ?? activeBranch?.location?.latitude
        ?? currentOrganization?.latitude
        ?? 12.115,
      lng: order.deliveryLocation?.longitude
        ?? activeBranch?.location?.longitude
        ?? currentOrganization?.longitude
        ?? -86.2362,
    });
  }, [order.deliveryLocation, activeBranch, currentOrganization]);

  const markerRef = useRef<any>(null);

  const handleClose = () => {
    dispatch(closeLocationDialog());
  };

  const handleSave = useCallback(async () => {
    if (!location?.lat || !location?.lng) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await calculate(location.lat, location.lng);

      if (!response?.distance || !response?.duration) {
        throw new Error();
      }

      //
      const distanceInKm = response.distance?.value / 1000;
      const maxDistanceKm = currentOrganization?.settings?.services?.delivery?.maxDistanceKm;

      if (maxDistanceKm && distanceInKm > maxDistanceKm) {
        openSnackbar(
          `La cobertura de entrega no está disponible para tu zona. El rango máximo es de ${maxDistanceKm} km.`,
          "error"
        );
        return;
      }
      //

      const deliveryLocation = {
        latitude: location.lat,
        longitude: location.lng,
        distance: response.distance,
        duration: response.duration,
      };

      dispatch(
        setDeliveryLocation({
          ...order.deliveryLocation,
          ...deliveryLocation,
        }),
      );

      const updateOrderResponse = await updateOrder({
        organizationId,
        order: {
          id: order.id,
          deliveryLocation: {
            ...order.deliveryLocation,
            ...deliveryLocation,
          },
        },
      });

      if (updateOrderResponse.error) {
        throw new Error();
      } else {
        openSnackbar("Dirección actualizada correctamente", "success");
        handleClose();
      }
    } catch (error) {
      openSnackbar("Error al actualizar la dirección", "error");
    } finally {
      setIsLoading(false);
    }
  }, [calculate, dispatch, location.lat, location.lng, order.deliveryLocation, organizationId]);


  const loadMap = (map: any, maps: any) => {
    mapRef.current = map;
    mapsRef.current = maps;

    const marker = new maps.Marker({
      position: location,
      map,
      draggable: true,
      zIndex: 2,
    });

    markerRef.current = marker;

    marker.addListener("dragend", () => {
      const newLatitude = marker.getPosition().lat();
      const newLongitude = marker.getPosition().lng();
      setLocation({ lat: newLatitude, lng: newLongitude });
    });

    directionsRendererRef.current = new maps.DirectionsRenderer({
      polylineOptions: {
        strokeColor: "#1976d2",
        strokeWeight: 5,
      },
      suppressMarkers: true,
    });

    directionsRendererRef.current.setMap(map);

    if (activeBranch?.location) {
      new maps.Marker({
        position: {
          lat: activeBranch.location.latitude,
          lng: activeBranch.location.longitude,
        },
        map,
        icon: {
          url: "https://firebasestorage.googleapis.com/v0/b/menuapp-b44a8.appspot.com/o/assets%2Fstore-icon-.webp?alt=media&token=7034e915-c44d-45e9-bc45-f9494de28354",
          scaledSize: new maps.Size(40, 40),
        },
        zIndex: 1,
      });
      mapRef.current.panTo(location);
    } else {
      new maps.Marker({
        position: {
          lat: currentOrganization?.latitude,
          lng: currentOrganization?.longitude,
        },
        map,
        icon: {
          url: "https://firebasestorage.googleapis.com/v0/b/menuapp-b44a8.appspot.com/o/assets%2Fstore-icon-.webp?alt=media&token=7034e915-c44d-45e9-bc45-f9494de28354",
          scaledSize: new maps.Size(40, 40),
        },
        zIndex: 1,
      });
      mapRef.current.panTo(location);
    }
  };

  const drawRoute = useCallback(() => {
    if (!mapsRef.current || !mapRef.current || !directionsRendererRef.current) return;

    const originLat = activeBranch?.location?.latitude ?? currentOrganization?.latitude;
    const originLng = activeBranch?.location?.longitude ?? currentOrganization?.longitude;

    if (!originLat || !originLng) return;

    const directionsService = new mapsRef.current.DirectionsService();

    directionsService.route(
      {
        origin: {
          lat: originLat,
          lng: originLng,
        },
        destination: {
          lat: location.lat,
          lng: location.lng,
        },
        travelMode: mapsRef.current.TravelMode.DRIVING,
      },
      (result: any, status: any) => {
        if (status === "OK") {
          directionsRendererRef.current.setDirections(result);
        }
      }
    );
  }, [location, activeBranch, currentOrganization]);

  useEffect(() => {
    drawRoute();
  }, [drawRoute]);


  return (
    <Dialog fullScreen onClose={handleClose} open={isLocationDialogOpen}>
      <Box sx={{ height: "100dvh", width: "100dvw", position: "relative", display: "flex", flexDirection: "column" }}>
        <Stack flex={1}>
          <GoogleMapReactAny
            bootstrapURLKeys={{ key: import.meta.env.VITE_FIREBASE_API_KEY }}
            center={location}
            defaultZoom={12}
            onGoogleApiLoaded={({ map, maps }) => loadMap(map, maps)}
            yesIWantToUseGoogleMapApiInternals
            options={(maps) => ({
              zoomControl: true,
              mapTypeControl: true,
              fullscreenControl: false,
              mapTypeControlOptions: {
                style: maps.MapTypeControlStyle.HORIZONTAL_BAR,
                position: maps.ControlPosition.TOP_LEFT,
              },
              zoomControlOptions: {
                position: maps.ControlPosition.TOP_LEFT,
              },
            })}
          />
        </Stack>
        <Stack>
          <Paper elevation={8} sx={{ p: 3 }}>
            <LoadingButton variant="contained" color="primary" size="large" fullWidth onClick={handleSave} loading={isLoading}>
              Guardar
            </LoadingButton>
          </Paper>
        </Stack>
        <IconButton
          component={Paper}
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 24,
            right: 24,
            backgroundColor: "white !important",
          }}
        >
          <HugeiconsIcon icon={Cancel01Icon} color={palette.primary.main} />
        </IconButton>
      </Box>
    </Dialog>
  );
};

export default LocationDialog;
